import { useState, useEffect, useMemo, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronDown, ChevronUp, ChevronLeft, CheckCircle, Loader, Loader2, AlertCircle,
  FileText, Search, X, Trash2, TrendingUp, Info, ArrowRightLeft
} from "lucide-react";
import { useTaxYear } from "../contexts/TaxYearContext";
import { useAuth } from "../contexts/AuthContext";
import ArchiveButton from "../components/ArchiveButton";
import { VaultProvenanceBadge } from "../components/VaultProvenanceBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const CATEGORIES = [
  "CACFP Income", "CACFP Food Expense", "CACFP Non-Food Expense",
  "CACFP Utilities", "CACFP Use Charge for Space", "Maintenance Expense",
  "Co-Payments (Parent Fees)", "Subsidy 1099 Income", "Payroll Expense",
  "Insurance", "SBA Loan Payment",
  "Business Loan - Bank/Credit", "Business Loan - Vehicle", "Business Loan - Equipment",
  "Rent Payment", "Mortgage Payment",
  "Loan Interest",
  "Real Estate / Property Tax",
  "Advertising / Marketing",
  "Contract Labor (1099)",
  "Office Supplies",
  "Professional Services",
  "Continuing Education / Staff Training",
  "Card Processing Fees",
  "Vehicle / Mileage",
  "Vehicle Expense",
  "Travel & Meals",
  "Food Expense (Non-CACFP)",
  "Non-Food Expense (Non-CACFP)",
  "Interest & Dividend Income",
  "Other Income", "Other Expense",
  "Grant Income", "Grant Expense",
  "Equipment Purchase",
];

// Subcategory cards (mirrors DOCUMENT_TYPES on backend)
const SUBCATEGORIES = [
  { type: "Bank Statement", icon: "🏦", color: "text-blue-600", border: "border-blue-200",
    samples: [{ name: "Wells_Fargo_Jan.pdf", items: 18, amount: 8420, approved: 14 },
              { name: "PNC_Savings_Jan.pdf", items: 5, amount: 1150, approved: 3 }],
    sampleLineItems: [
      { date: "01/05", desc: "PARENT FEE - JOHNSON", amt: 185.00, cat: "Co-Payments (Parent Fees)", type: "income", approved: true },
      { date: "01/12", desc: "PECO ELECTRIC", amt: 238.10, cat: "CACFP Utilities", type: "expense", approved: false },
      { date: "01/20", desc: "CACFP MONTHLY CLAIM", amt: 5200.00, cat: "CACFP Income", type: "income", approved: false },
    ]},
  { type: "CACFP Report", icon: "📋", color: "text-purple-600", border: "border-purple-200",
    samples: [{ name: "CACFP_December.pdf", items: 4, amount: 5200, approved: 4 },
              { name: "CACFP_November.pdf", items: 4, amount: 4980, approved: 0 }],
    sampleLineItems: [
      { date: "12/05", desc: "CACFP MONTHLY REIMBURSEMENT", amt: 5200.00, cat: "CACFP Income", type: "income", approved: true },
      { date: "12/15", desc: "CACFP MEAL CLAIM ADJUSTMENT", amt: 320.00, cat: "CACFP Income", type: "income", approved: false },
      { date: "12/28", desc: "CACFP TIER 1 BONUS", amt: 180.00, cat: "CACFP Income", type: "income", approved: false },
    ]},
  { type: "Subsidy/1099 Report", icon: "📄", color: "text-emerald-600", border: "border-emerald-200",
    samples: [{ name: "Subsidy_1099_2024.pdf", items: 12, amount: 8400, approved: 12 },
              { name: "1099-NEC_2024.pdf", items: 1, amount: 5800, approved: 0 }],
    sampleLineItems: [
      { date: "01/10", desc: "SUBSIDY PAYMENT", amt: 700.00, cat: "Subsidy 1099 Income", type: "income", approved: true },
      { date: "01/25", desc: "STATE CHILDCARE SUBSIDY", amt: 480.00, cat: "Subsidy 1099 Income", type: "income", approved: false },
      { date: "01/31", desc: "1099-NEC CONTRACT INCOME", amt: 2200.00, cat: "Subsidy 1099 Income", type: "income", approved: false },
    ]},
  { type: "Utility Bill", icon: "⚡", color: "text-amber-600", border: "border-amber-200",
    samples: [{ name: "PECO_Electric_Jan.pdf", items: 1, amount: 238, approved: 1 },
              { name: "PGW_Gas_Jan.pdf", items: 1, amount: 142, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "PECO ELECTRIC SERVICE", amt: 238.10, cat: "CACFP Utilities", type: "expense", approved: true },
      { date: "01/08", desc: "PGW NATURAL GAS", amt: 142.30, cat: "CACFP Utilities", type: "expense", approved: false },
      { date: "01/15", desc: "PHILA WATER DEPT", amt: 96.05, cat: "CACFP Utilities", type: "expense", approved: false },
    ]},
  { type: "Receipt/Invoice", icon: "🧾", color: "text-rose-600", border: "border-rose-200",
    samples: [{ name: "Sams_Club_Receipt.pdf", items: 8, amount: 412, approved: 5 },
              { name: "Curriculum_Order.pdf", items: 3, amount: 285, approved: 0 }],
    sampleLineItems: [
      { date: "01/10", desc: "HOME DEPOT - SUPPLIES", amt: 42.18, cat: "Maintenance Expense", type: "expense", approved: true },
      { date: "01/17", desc: "STAPLES OFFICE SUPPLIES", amt: 28.99, cat: "Other Expense", type: "expense", approved: false },
      { date: "01/24", desc: "LAKESHORE CURRICULUM ORDER", amt: 285.00, cat: "Other Expense", type: "expense", approved: false },
    ]},
  { type: "Payroll Report", icon: "👥", color: "text-teal-600", border: "border-teal-200",
    samples: [{ name: "ADP_January.pdf", items: 6, amount: 4250, approved: 6 },
              { name: "Form_941_Q1.pdf", items: 1, amount: 1820, approved: 0 }],
    sampleLineItems: [
      { date: "01/15", desc: "STAFF WAGES - BIWEEKLY", amt: 3200.00, cat: "Payroll Expense", type: "expense", approved: true },
      { date: "01/15", desc: "FEDERAL PAYROLL TAX (941)", amt: 1820.00, cat: "Payroll Expense", type: "expense", approved: false },
      { date: "01/30", desc: "STAFF WAGES - BIWEEKLY", amt: 3200.00, cat: "Payroll Expense", type: "expense", approved: false },
    ]},
  { type: "Health Insurance", icon: "❤️", color: "text-pink-600", border: "border-pink-200",
    samples: [{ name: "Aetna_Premium_Jan.pdf", items: 3, amount: 950, approved: 3 },
              { name: "Dental_Vision_Jan.pdf", items: 2, amount: 60, approved: 0 }],
    sampleLineItems: [
      { date: "01/12", desc: "AETNA HEALTH PREMIUM", amt: 890.00, cat: "Insurance", type: "expense", approved: true },
      { date: "01/12", desc: "AETNA DENTAL ADD-ON", amt: 42.00, cat: "Insurance", type: "expense", approved: false },
      { date: "01/12", desc: "AETNA VISION ADD-ON", amt: 18.00, cat: "Insurance", type: "expense", approved: false },
    ]},
  { type: "Auto Insurance", icon: "🚗", color: "text-indigo-600", border: "border-indigo-200",
    samples: [{ name: "State_Farm_Auto.pdf", items: 1, amount: 312, approved: 1 },
              { name: "Commercial_Vehicle.pdf", items: 1, amount: 480, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "STATE FARM AUTO PREMIUM", amt: 312.00, cat: "Insurance", type: "expense", approved: true },
      { date: "01/05", desc: "STATE FARM ROADSIDE", amt: 18.00, cat: "Insurance", type: "expense", approved: false },
      { date: "01/05", desc: "COMMERCIAL VEHICLE PREMIUM", amt: 480.00, cat: "Insurance", type: "expense", approved: false },
    ]},
  { type: "Business Insurance", icon: "🏢", color: "text-sky-600", border: "border-sky-200",
    samples: [{ name: "Liability_Policy.pdf", items: 1, amount: 640, approved: 1 },
              { name: "Workers_Comp.pdf", items: 1, amount: 1200, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "GENERAL LIABILITY PREMIUM", amt: 640.00, cat: "Insurance", type: "expense", approved: true },
      { date: "01/05", desc: "WORKERS COMP PREMIUM", amt: 1200.00, cat: "Insurance", type: "expense", approved: false },
      { date: "01/05", desc: "PROPERTY COVERAGE", amt: 380.00, cat: "Insurance", type: "expense", approved: false },
    ]},
  { type: "Real Estate Taxes", icon: "🏛️", color: "text-stone-600", border: "border-stone-200",
    samples: [{ name: "County_Tax_Bill.pdf", items: 1, amount: 2400, approved: 1 },
              { name: "School_District_Tax.pdf", items: 1, amount: 1850, approved: 0 }],
    sampleLineItems: [
      { date: "01/28", desc: "COUNTY PROPERTY TAX", amt: 2400.00, cat: "Other Expense", type: "expense", approved: true },
      { date: "01/28", desc: "SCHOOL DISTRICT TAX", amt: 1850.00, cat: "Other Expense", type: "expense", approved: false },
      { date: "01/28", desc: "CITY/MUNICIPAL TAX", amt: 320.00, cat: "Other Expense", type: "expense", approved: false },
    ]},
  { type: "Maintenance & Repairs", icon: "🔧", color: "text-slate-600", border: "border-slate-200",
    samples: [{ name: "Roto_Rooter.pdf", items: 1, amount: 285, approved: 1 },
              { name: "HVAC_Service.pdf", items: 2, amount: 540, approved: 0 }],
    sampleLineItems: [
      { date: "01/10", desc: "HVAC SERVICE CALL", amt: 285.00, cat: "Maintenance Expense", type: "expense", approved: true },
      { date: "01/18", desc: "ROOF LEAK REPAIR", amt: 540.00, cat: "Maintenance Expense", type: "expense", approved: false },
      { date: "01/25", desc: "CLEANING SERVICE - MONTHLY", amt: 220.00, cat: "Maintenance Expense", type: "expense", approved: false },
    ]},
  { type: "Other Income", icon: "💰", color: "text-green-600", border: "border-green-200",
    samples: [{ name: "Late_Fees_Jan.pdf", items: 7, amount: 175, approved: 7 },
              { name: "Enrollment_Fees.pdf", items: 4, amount: 600, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "LATE PICK-UP FEE - SMITH", amt: 25.00, cat: "Other Income", type: "income", approved: true },
      { date: "01/15", desc: "NEW ENROLLMENT FEE", amt: 150.00, cat: "Other Income", type: "income", approved: false },
      { date: "01/22", desc: "REGISTRATION FEE", amt: 75.00, cat: "Other Income", type: "income", approved: false },
    ]},
  { type: "Food Purchases", icon: "🍎", color: "text-lime-600", border: "border-lime-200",
    samples: [{ name: "Sams_Club_Food.pdf", items: 14, amount: 612, approved: 10 },
              { name: "Costco_Food.pdf", items: 9, amount: 387, approved: 0 }],
    sampleLineItems: [
      { date: "01/03", desc: "SAMS CLUB - GROCERIES", amt: 245.18, cat: "CACFP Food Expense", type: "expense", approved: true },
      { date: "01/10", desc: "COSTCO - BULK FOOD", amt: 187.42, cat: "CACFP Food Expense", type: "expense", approved: false },
      { date: "01/17", desc: "MILK DELIVERY SERVICE", amt: 64.50, cat: "CACFP Food Expense", type: "expense", approved: false },
    ]},
  { type: "Phone & Internet", icon: "📞", color: "text-cyan-600", border: "border-cyan-200",
    samples: [{ name: "Comcast_Internet.pdf", items: 1, amount: 136, approved: 1 },
              { name: "Verizon_Cell.pdf", items: 1, amount: 98, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "COMCAST INTERNET", amt: 136.00, cat: "CACFP Utilities", type: "expense", approved: true },
      { date: "01/12", desc: "VERIZON BUSINESS CELL", amt: 98.00, cat: "CACFP Utilities", type: "expense", approved: false },
      { date: "01/20", desc: "BUSINESS LANDLINE", amt: 45.00, cat: "CACFP Utilities", type: "expense", approved: false },
    ]},
  { type: "Credit Card Statement", icon: "💳", color: "text-violet-600", border: "border-violet-200",
    samples: [{ name: "Amex_Business_Jan.pdf", items: 14, amount: 2410, approved: 8 },
              { name: "Visa_Business_Jan.pdf", items: 9, amount: 1240, approved: 0 }],
    sampleLineItems: [
      { date: "01/05", desc: "INTEREST CHARGE", amt: 42.18, cat: "Loan Interest", type: "expense", approved: true },
      { date: "01/10", desc: "TARGET - SUPPLIES", amt: 78.42, cat: "Office Supplies", type: "expense", approved: false },
      { date: "01/15", desc: "FACEBOOK ADS", amt: 250.00, cat: "Advertising / Marketing", type: "expense", approved: false },
    ]},
  { type: "Vehicle Expense", icon: "🚙", color: "text-amber-700", border: "border-amber-200",
    samples: [{ name: "Shell_Fuel_Receipt.pdf", items: 1, amount: 58, approved: 0 },
              { name: "Pep_Boys_Tires_2025.pdf", items: 1, amount: 420, approved: 0 }],
    sampleLineItems: [
      { date: "01/08", desc: "SHELL GAS", amt: 58.40, cat: "Vehicle Expense", type: "expense", approved: false },
      { date: "01/15", desc: "PEP BOYS - 4 TIRES", amt: 420.00, cat: "Vehicle Expense", type: "expense", approved: false },
      { date: "01/22", desc: "VEHICLE REGISTRATION", amt: 86.00, cat: "Vehicle Expense", type: "expense", approved: false },
    ]},
  { type: "Manual Entry / Cash Transactions", icon: "✏️", color: "text-orange-600", border: "border-orange-200",
    samples: [{ name: "Cash_Receipts_Jan.pdf", items: 6, amount: 320, approved: 6 }],
    sampleLineItems: [
      { date: "01/04", desc: "PARENT CASH PAYMENT", amt: 75.00, cat: "Co-Payments (Parent Fees)", type: "income", approved: true },
      { date: "01/12", desc: "PETTY CASH - SUPPLIES", amt: 24.50, cat: "Office Supplies", type: "expense", approved: true },
      { date: "01/22", desc: "FIELD TRIP CASH ADMISSION", amt: 48.00, cat: "Travel & Meals", type: "expense", approved: false },
    ]},
  { type: "Misc Upload", icon: "📁", color: "text-gray-600", border: "border-gray-200",
    samples: [{ name: "SBA_Loan_Statement.pdf", items: 1, amount: 1200, approved: 1 },
              { name: "License_Renewal.pdf", items: 1, amount: 350, approved: 0 }],
    sampleLineItems: [
      { date: "01/15", desc: "SBA LOAN PAYMENT", amt: 1200.00, cat: "SBA Loan Payment", type: "expense", approved: true },
      { date: "01/22", desc: "STATE LICENSE RENEWAL", amt: 350.00, cat: "Other Expense", type: "expense", approved: false },
      { date: "01/28", desc: "STAFF TRAINING REGISTRATION", amt: 195.00, cat: "Other Expense", type: "expense", approved: false },
    ]},
  { type: "Grant Documents", icon: "🏅", color: "text-emerald-600", border: "border-emerald-200",
    samples: [{ name: "Stabilization_Grant_Q2.pdf", items: 1, amount: 5000, approved: 0 }],
    sampleLineItems: [
      { date: "03/15", desc: "CHILD CARE STABILIZATION GRANT - Q2", amt: 5000.00, cat: "Grant Income", type: "income", approved: false },
      { date: "04/10", desc: "GRANT-FUNDED CLASSROOM SUPPLIES", amt: 380.00, cat: "Grant Expense", type: "expense", approved: false },
    ]},
  { type: "Equipment Receipts", icon: "🖥️", color: "text-blue-600", border: "border-blue-200",
    samples: [{ name: "Playground_Equipment_Invoice.pdf", items: 2, amount: 4800, approved: 0 }],
    sampleLineItems: [
      { date: "02/20", desc: "COMMERCIAL PLAYGROUND STRUCTURE", amt: 4200.00, cat: "Equipment Purchase", type: "expense", approved: false },
      { date: "02/20", desc: "INSTALLATION FEE", amt: 600.00, cat: "Equipment Purchase", type: "expense", approved: false },
    ]},
];

export default function Transactions() {
  const [docs, setDocs] = useState([]);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null);
  const [error, setError] = useState("");
  const [landingSearch, setLandingSearch] = useState("");
  const [scoreOpen, setScoreOpen] = useState(false);
  const [focusTxnId, setFocusTxnId] = useState(null);

  const fetchAll = async () => {
    try {
      const [d, t] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/transactions`)
      ]);
      setDocs(d.data || []);
      setTxns(t.data || []);
    } catch {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const subStats = useMemo(() => {
    const map = {};
    SUBCATEGORIES.forEach(s => { map[s.type] = { docCount: 0, txnCount: 0, total: 0, approvedCount: 0 }; });
    docs.forEach(d => { if (map[d.document_type]) map[d.document_type].docCount += 1; });
    txns.forEach(t => {
      const d = docs.find(x => x._id === t.document_id);
      const type = d?.document_type;
      if (type && map[type]) {
        map[type].txnCount += 1;
        map[type].total += Math.abs(t.amount || 0);
        if (t.approved) map[type].approvedCount += 1;
      }
    });
    return map;
  }, [docs, txns]);

  const grandTotals = useMemo(() => {
    let income = 0, expenses = 0, total = 0, approved = 0;
    txns.forEach(t => {
      total += 1;
      if (t.approved) approved += 1;
      if (t.type === "income") income += Math.abs(t.amount || 0);
      else expenses += Math.abs(t.amount || 0);
    });
    return { income, expenses, net: income - expenses, total, approved };
  }, [txns]);

  // Item 2 of pre-3d roadmap — count of OCR-flagged rows that fell back to
  // "Other Expense" / "Other Income". Shown as an amber banner at the top of
  // the Hub so the user can fix them quickly. Also drives the sidebar badge.
  const needsCategorizationCount = useMemo(
    () => txns.filter(t => t.needs_categorization).length,
    [txns]
  );
  useEffect(() => {
    try {
      window.dispatchEvent(new CustomEvent("transactions:categorization:changed",
        { detail: { count: needsCategorizationCount } }));
    } catch { /* ignore */ }
  }, [needsCategorizationCount]);

  if (loading) {
    return <div className="p-8 max-w-[1600px] mx-auto"><div className="text-slate-400 text-sm">Loading…</div></div>;
  }

  if (activeType) {
    return (
      <DrillDown
        type={activeType}
        docs={docs.filter(d => d.document_type === activeType)}
        allTxns={txns}
        focusTxnId={focusTxnId}
        onBack={() => { setActiveType(null); setFocusTxnId(null); }}
        onRefresh={fetchAll}
        setError={setError}
        error={error}
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Transactions</h1>
        <p className="text-slate-500 text-sm mt-1">Click a category to review extracted line items, assign categories, and approve them.</p>
      </div>

      {needsCategorizationCount > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="needs-cat-banner">
          <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-amber-900 text-sm" style={{ fontFamily: "Manrope" }}>
              {needsCategorizationCount} transaction{needsCategorizationCount === 1 ? "" : "s"} need{needsCategorizationCount === 1 ? "s" : ""} a more specific category
            </p>
            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
              Our OCR auto-categorizer used the generic "Other Expense" or "Other Income" fallback for these rows because it wasn't confident.
              Click into the relevant card below (Bank Statement, Receipt/Invoice, etc.) and pick a more specific category — it improves your Schedule C accuracy.
            </p>
          </div>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-xl px-5 py-4 shadow-sm">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">How to review each item once extracted</p>
        <ol className="text-sm text-slate-700 space-y-1.5 list-decimal list-inside">
          <li>Pick a <span className="font-semibold">Category</span> from the dropdown (Insurance, CACFP Income, etc.)</li>
          <li>Choose <span className="font-semibold">Type</span> — Income or Expense</li>
          <li>Click the <span className="font-semibold">green circle</span> to ✓ approve once verified against your hardcopy</li>
          <li>Use bulk buttons to approve checked items or all at once</li>
        </ol>
        <p className="text-sm font-semibold text-slate-800 mt-3 pt-3 border-t border-slate-200">Approved items automatically flow to your Income, Expenses, CACFP Overview, and Tax Prep reports.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />{error}<button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Optional global search across all line items */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search any line item across all documents (vendor, description)..."
            value={landingSearch}
            onChange={e => setLandingSearch(e.target.value)}
            data-testid="hub-landing-search"
            className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
          />
        </div>
        {landingSearch && (
          <div className="mt-3 max-h-64 overflow-y-auto">
            {(() => {
              const matches = txns.filter(t =>
                (t.description || "").toLowerCase().includes(landingSearch.toLowerCase()) ||
                (t.category || "").toLowerCase().includes(landingSearch.toLowerCase())
              );
              if (matches.length === 0) return <p className="text-xs text-slate-400 text-center py-4">No matching items.</p>;
              return (
                <ul className="divide-y divide-slate-100 text-sm">
                  {matches.slice(0, 50).map(t => {
                    const d = docs.find(x => x._id === t.document_id);
                    return (
                      <li key={t._id}
                        onClick={() => { if (d) { setActiveType(d.document_type); setLandingSearch(""); } }}
                        className="py-2 px-2 hover:bg-slate-50 cursor-pointer flex items-center gap-3">
                        <span className="text-xs text-slate-400 w-16">{t.date}</span>
                        <span className="flex-1 truncate">{t.description}</span>
                        <span className="text-xs text-slate-500">{t.category}</span>
                        <span className={`text-xs font-semibold ${t.type === "income" ? "text-emerald-600" : "text-red-600"}`}>{fmt(Math.abs(t.amount))}</span>
                        <span className="text-xs text-slate-400 truncate max-w-[160px]">{d?.original_name}</span>
                      </li>
                    );
                  })}
                  {matches.length > 50 && <li className="py-2 text-center text-xs text-slate-400">+{matches.length - 50} more — refine your search</li>}
                </ul>
              );
            })()}
          </div>
        )}
      </div>

      {/* See What's Affecting Your Score — collapsed by default */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="affecting-score-panel">
        <div
          onClick={() => setScoreOpen(o => !o)}
          data-testid="affecting-score-toggle"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setScoreOpen(o => !o); }}
          className={`w-full flex items-center justify-between px-5 py-3.5 text-white cursor-pointer hover:opacity-95 transition-opacity ${scoreOpen ? "rounded-t-xl" : "rounded-xl"}`}
          style={{ background: "linear-gradient(90deg, #1E3A8A 0%, #1E40AF 100%)" }}
        >
          <div className="flex items-center gap-3 flex-1">
            <TrendingUp className="w-5 h-5 text-blue-200 flex-shrink-0" />
            <span className="font-bold text-sm" style={{ fontFamily: "Manrope" }}>See What's Affecting Your Score</span>
            {/* Info tooltip — stopPropagation so clicking the icon doesn't collapse the panel */}
            <div
              className="relative group flex items-center"
              data-testid="affecting-score-info"
              onClick={(e) => e.stopPropagation()}
            >
              <Info className="w-4 h-4 text-blue-200 hover:text-white cursor-help transition-colors" />
              <div className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 w-72 z-[100]
                              opacity-0 group-hover:opacity-100 transition-opacity duration-200
                              bg-slate-900 text-white text-xs rounded-lg shadow-2xl border border-slate-700 p-3">
                <p className="font-bold text-sm mb-1.5" style={{ fontFamily: "Manrope" }}>How your score works</p>
                <p className="text-slate-200 leading-relaxed">
                  This score reflects the % of extracted line items you've approved (✓). Items only count toward your final tax report once approved. Approve items below to raise your score.
                </p>
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-y-[6px] border-y-transparent border-r-[6px] border-r-slate-900" />
              </div>
            </div>
            <span className="text-blue-200 text-xs font-medium">
              {grandTotals.total > 0
                ? `${grandTotals.approved} of ${grandTotals.total} approved · ${Math.round((grandTotals.approved / grandTotals.total) * 100)}%`
                : "No items yet"}
            </span>
          </div>
          {scoreOpen ? <ChevronUp className="w-5 h-5 text-blue-200" /> : <ChevronDown className="w-5 h-5 text-blue-200" />}
        </div>

        {scoreOpen && (
          <div className="p-5 space-y-5">
            {/* How your score is built */}
            <div>
              <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-3">How your score is built</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead style={{ background: "#1E3A8A" }}>
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-white uppercase tracking-wider">Subcategory</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-white uppercase tracking-wider w-1/3">% Complete</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold text-white uppercase tracking-wider">Items</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold text-white uppercase tracking-wider">Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SUBCATEGORIES.filter(s => subStats[s.type].txnCount > 0).map(sub => {
                      const s = subStats[sub.type];
                      const pct = s.txnCount > 0 ? Math.round((s.approvedCount / s.txnCount) * 100) : 0;
                      const barColor = pct === 100 ? "bg-emerald-500" : pct >= 67 ? "bg-emerald-500" : pct >= 33 ? "bg-amber-500" : "bg-red-500";
                      const txtColor = pct === 100 ? "text-emerald-600" : pct >= 67 ? "text-emerald-600" : pct >= 33 ? "text-amber-600" : "text-red-600";
                      return (
                        <tr
                          key={sub.type}
                          onClick={() => setActiveType(sub.type)}
                          data-testid={`affecting-score-row-${sub.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          className="border-t border-slate-100 hover:bg-blue-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            <span className="mr-2">{sub.icon}</span>{sub.type}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className={`text-xs font-bold ${txtColor} w-10 text-right`}>{pct}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-600 text-sm">{s.txnCount}</td>
                          <td className={`px-4 py-3 text-right text-sm font-semibold ${txtColor}`}>{s.approvedCount} / {s.txnCount}</td>
                        </tr>
                      );
                    })}
                    {SUBCATEGORIES.every(s => subStats[s.type].txnCount === 0) && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400 text-sm">No items yet — upload a document to start.</td></tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-bold border-t-2 border-slate-300">
                      <td className="px-4 py-3 text-slate-900">Total Score</td>
                      <td className="px-4 py-3">
                        <span className="text-slate-900 text-base">
                          {grandTotals.total > 0 ? `${Math.round((grandTotals.approved / grandTotals.total) * 100)}%` : "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-900">{grandTotals.total}</td>
                      <td className="px-4 py-3 text-right text-slate-900">{grandTotals.approved} / {grandTotals.total}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Top issues costing you score */}
            {(() => {
              const unapproved = txns
                .filter(t => !t.approved)
                .map(t => {
                  const d = docs.find(x => x._id === t.document_id);
                  return { ...t, _docType: d?.document_type, _docName: d?.original_name };
                })
                .filter(t => t._docType)
                .sort((a, b) => Math.abs(b.amount || 0) - Math.abs(a.amount || 0));
              if (unapproved.length === 0) {
                return (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <p className="text-emerald-800 text-sm font-semibold">All items approved — you're tax-ready!</p>
                  </div>
                );
              }
              return (
                <div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Top issues costing you score</p>
                    <span className="text-xs text-slate-400">(highest impact first)</span>
                  </div>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Item</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Subcategory</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                          <th className="px-4 py-2 w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {unapproved.slice(0, 10).map(t => (
                          <tr key={t._id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-2.5 text-slate-700">
                              <p className="font-medium truncate max-w-[280px]">{t.description}</p>
                              <p className="text-[11px] text-slate-400">{t.date} · {t._docName}</p>
                            </td>
                            <td className="px-4 py-2.5 text-slate-500 text-xs">{t._docType}</td>
                            <td className="px-4 py-2.5 text-right font-semibold text-red-600">{fmt(Math.abs(t.amount || 0))}</td>
                            <td className="px-4 py-2.5 text-right">
                              <button
                                onClick={() => { setFocusTxnId(t._id); setActiveType(t._docType); }}
                                data-testid={`affecting-score-review-${t._id}`}
                                className="text-xs font-semibold text-[#2563EB] hover:text-[#1D4ED8] hover:underline"
                              >
                                Review →
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {unapproved.length > 10 && (
                    <p className="text-xs text-slate-400 mt-2 text-center">+{unapproved.length - 10} more — click a row above to start with that subcategory</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Subcategory cards 5×3 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {SUBCATEGORIES.map(sub => {
          const s = subStats[sub.type];
          const allApproved = s.txnCount > 0 && s.approvedCount === s.txnCount;
          const inProg = s.approvedCount > 0 && s.approvedCount < s.txnCount;
          return (
            <button
              key={sub.type}
              onClick={() => setActiveType(sub.type)}
              data-testid={`hub-card-${sub.type.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`text-left bg-white border ${sub.border} rounded-xl p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${s.docCount === 0 ? "opacity-60" : ""}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="text-2xl">{sub.icon}</div>
                {allApproved && <CheckCircle className="w-4 h-4 text-emerald-500" />}
              </div>
              <p className={`font-bold text-sm leading-tight ${sub.color}`} style={{ fontFamily: "Manrope" }}>{sub.type}</p>
              <div className="mt-3 space-y-0.5 text-xs">
                <p className="text-slate-500">{s.docCount} {s.docCount === 1 ? "document" : "documents"}</p>
                <p className="text-slate-500">{s.txnCount} {s.txnCount === 1 ? "item" : "items"}</p>
                <p className="text-slate-900 font-semibold pt-0.5">{fmt(s.total)}</p>
                {s.txnCount > 0 ? (
                  <p className={`text-xs font-semibold pt-0.5 ${allApproved ? "text-emerald-600" : inProg ? "text-amber-600" : "text-slate-400"}`}>
                    {allApproved ? "✓ All approved" : `${s.approvedCount} of ${s.txnCount} approved`}
                  </p>
                ) : (
                  <p className="text-slate-300 text-xs pt-0.5">No items yet</p>
                )}
              </div>
              {s.docCount === 0 && sub.samples && (
                <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                  <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">Example preview</p>
                  <div className="opacity-50 space-y-1.5">
                    {sub.samples.map((ex, idx) => (
                      <div key={idx} className="flex items-center gap-1.5 text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5">
                        <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="truncate font-medium text-slate-600">{ex.name}</p>
                          <p className="text-[10px] text-slate-400">{ex.items} items • {fmt(ex.amount)} • {ex.approved} of {ex.items}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Grand Total */}
      <div className="bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Grand Total — All Categories</p>
            <div className="grid grid-cols-3 gap-8">
              <div>
                <p className="text-xs text-slate-500">Total Income</p>
                <p className="text-2xl font-bold text-emerald-600 mt-0.5" style={{ fontFamily: "Manrope" }}>{fmt(grandTotals.income)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600 mt-0.5" style={{ fontFamily: "Manrope" }}>{fmt(grandTotals.expenses)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Net</p>
                <p className={`text-2xl font-bold mt-0.5 ${grandTotals.net >= 0 ? "text-emerald-600" : "text-red-600"}`} style={{ fontFamily: "Manrope" }}>
                  {grandTotals.net >= 0 ? "+" : ""}{fmt(grandTotals.net)}
                </p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 mb-1">Approval progress</p>
            <p className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {grandTotals.approved} <span className="text-slate-400 font-normal">of</span> {grandTotals.total}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              {grandTotals.total > 0 ? `${Math.round((grandTotals.approved / grandTotals.total) * 100)}% complete` : "No items yet"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const STATUS_ICON = { processed: CheckCircle, pending: Loader, processing: Loader, error: AlertCircle };
const STATUS_CLR = { processed: "text-emerald-500", pending: "text-amber-500", processing: "text-blue-500", error: "text-red-500" };

function DrillDown({ type, docs, allTxns, focusTxnId, onBack, onRefresh, setError, error }) {
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [filterApproval, setFilterApproval] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [printing, setPrinting] = useState(false);
  const { taxYear } = useTaxYear();
  const { user } = useAuth();

  // Ship Gate 3a-prime — Print Selected → branded PDF of only checked items,
  // grouped by source document. This is the audit-grade report shape that will
  // also become the auto-archived artifact when Gate 3d ships.
  const handlePrintSelected = async () => {
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error("Check at least one item to include"); return; }
    setPrinting(true);
    try {
      const { data } = await axios.post(
        `${API}/reports/selected-pdf`,
        { document_type: type, transaction_ids: ids, tax_year: taxYear },
        { responseType: "blob", withCredentials: true }
      );
      const blobUrl = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const center = (user?.center_name || "center").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "").slice(0, 40);
      const card = type.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      a.href = blobUrl;
      a.download = `${card}-selected-${center}-${taxYear}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Report ready (${ids.length} item${ids.length === 1 ? "" : "s"})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate report");
    } finally {
      setPrinting(false);
    }
  };

  const sub = SUBCATEGORIES.find(s => s.type === type) || {};

  const txnsByDoc = useMemo(() => {
    const map = {};
    docs.forEach(d => { map[d._id] = []; });
    allTxns.forEach(t => { if (map[t.document_id]) map[t.document_id].push(t); });
    return map;
  }, [docs, allTxns]);

  // If a focus txn is provided, auto-expand its parent doc and scroll the row into view
  useEffect(() => {
    if (!focusTxnId) return;
    const focusTxn = allTxns.find(t => t._id === focusTxnId);
    if (!focusTxn) return;
    setExpanded(prev => ({ ...prev, [focusTxn.document_id]: true }));
    // scroll & highlight after DOM updates
    setTimeout(() => {
      const el = document.querySelector(`[data-row-id="${focusTxnId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-400", "bg-amber-50");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-amber-400", "bg-amber-50");
        }, 2400);
      }
    }, 250);
  }, [focusTxnId, allTxns]);

  const filteredDocs = useMemo(() => {
    let list = [...docs];
    if (search) list = list.filter(d => d.original_name?.toLowerCase().includes(search.toLowerCase()));
    if (filterStatus) list = list.filter(d => d.processing_status === filterStatus);
    if (filterApproval) {
      list = list.filter(d => {
        const items = txnsByDoc[d._id] || [];
        if (items.length === 0) return false;
        const allA = items.every(t => t.approved);
        const someA = items.some(t => t.approved);
        if (filterApproval === "all") return allA;
        if (filterApproval === "none") return !someA;
        if (filterApproval === "partial") return someA && !allA;
        return true;
      });
    }
    list.sort((a, b) => {
      if (sortBy === "recent") return new Date(b.upload_date) - new Date(a.upload_date);
      if (sortBy === "oldest") return new Date(a.upload_date) - new Date(b.upload_date);
      if (sortBy === "name") return (a.original_name || "").localeCompare(b.original_name || "");
      if (sortBy === "amount") {
        const ta = (txnsByDoc[a._id] || []).reduce((s, t) => s + Math.abs(t.amount || 0), 0);
        const tb = (txnsByDoc[b._id] || []).reduce((s, t) => s + Math.abs(t.amount || 0), 0);
        return tb - ta;
      }
      if (sortBy === "items") return (txnsByDoc[b._id]?.length || 0) - (txnsByDoc[a._id]?.length || 0);
      return 0;
    });
    return list;
  }, [docs, search, filterStatus, filterApproval, sortBy, txnsByDoc]);

  const updateTxn = async (id, patch) => {
    // Pre-flight prohibited-cost validator (FNS § VII A 4) — only when description or category changes.
    if (patch.description !== undefined || patch.category !== undefined) {
      const txn = (txnsByDoc && Object.values(txnsByDoc).flat().find(t => t._id === id)) || {};
      const nextDesc = patch.description !== undefined ? patch.description : (txn.description || "");
      const nextCat = patch.category !== undefined ? patch.category : (txn.category || "");
      try {
        const res = await axios.post(`${API}/cacfp/validate-cost`,
          { description: nextDesc, category: nextCat },
          { withCredentials: true });
        if (res.data?.prohibited) {
          if (res.data?.category_is_cacfp) {
            // Hard block: cannot save into a CACFP_* category when description hits a prohibited pattern.
            toast.error("Cannot save into CACFP category", {
              description: res.data.warning || res.data.reason || "Matches an FNS § VII A 4 prohibited pattern. Re-categorize as a regular business expense to save.",
              duration: 6000,
            });
            await onRefresh(); // revert any optimistic UI by re-pulling truth
            return;
          }
          // Soft warning for non-CACFP saves
          toast.warning("FNS-prohibited pattern detected", {
            description: res.data.warning || "Safe to save as a regular business expense, but cannot be claimed against CACFP reimbursement.",
            duration: 5000,
          });
        }
      } catch {
        // Validator failure is non-fatal — proceed with save to avoid blocking the user.
      }
    }
    try {
      await axios.patch(`${API}/transactions/${id}`, patch);
      await onRefresh();
    } catch { setError("Update failed"); }
  };

  const bulkApprove = async (ids, approved = true) => {
    if (!ids.length) return;
    try {
      await axios.post(`${API}/transactions/bulk-update`, { ids, approved });
      setSelected({});
      await onRefresh();
    } catch { setError("Bulk update failed"); }
  };

  const deleteTxn = async (id) => {
    try {
      await axios.delete(`${API}/transactions/${id}`);
      await onRefresh();
    } catch { setError("Delete failed"); }
  };

  const addLineItem = async (docId) => {
    try {
      await axios.post(`${API}/transactions`, {
        document_id: docId,
        date: new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" }),
        description: "",
        amount: 0,
        type: "expense",
        category: "Other Expense",
      });
      await onRefresh();
    } catch { setError("Could not add line item"); }
  };

  const moveTxn = async (txnId, targetType) => {
    if (!targetType || targetType === type) return;
    try {
      await axios.patch(`${API}/transactions/${txnId}/move`, { document_type: targetType });
      await onRefresh();
    } catch { setError("Could not move line item"); }
  };

  const bulkMove = async (ids, targetType) => {
    if (!targetType || targetType === type || ids.length === 0) return;
    try {
      await axios.post(`${API}/transactions/bulk-move`, { transaction_ids: ids, document_type: targetType });
      setSelected({});
      await onRefresh();
    } catch { setError("Bulk move failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-5">
      <button
        onClick={onBack}
        data-testid="drilldown-back-btn"
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Transactions
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{sub.icon}</span>
          <div>
            <h1 className={`text-2xl font-extrabold ${sub.color}`} style={{ fontFamily: "Manrope" }}>{type}</h1>
            <p className="text-slate-500 text-sm">{docs.length} {docs.length === 1 ? "document" : "documents"} uploaded</p>
          </div>
        </div>
        {docs.length > 0 && (
          <div className="flex items-center gap-2">
            <button onClick={() => { const n = {}; filteredDocs.forEach(d => { n[d._id] = true; }); setExpanded(n); }}
              data-testid="expand-all-btn"
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Expand all</button>
            <button onClick={() => setExpanded({})}
              data-testid="collapse-all-btn"
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">Collapse all</button>
            <button onClick={() => {
                // Select-all across all visible docs in the drill-down
                const n = { ...selected };
                filteredDocs.forEach(d => {
                  (allTxns.filter(t => t.document_id === d._id) || []).forEach(t => { n[t._id] = true; });
                });
                setSelected(n);
              }}
              data-testid="drilldown-select-all-btn"
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
              Select all
            </button>
            <button onClick={() => setSelected({})}
              data-testid="drilldown-clear-selection-btn"
              className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
              Clear
            </button>
            <button
              onClick={handlePrintSelected}
              disabled={printing || Object.values(selected).filter(Boolean).length === 0}
              data-testid="drilldown-print-selected-btn"
              title={Object.values(selected).filter(Boolean).length === 0 ? "Check items first" : `Print ${Object.values(selected).filter(Boolean).length} selected item(s) as a branded PDF report`}
              className="flex items-center gap-1.5 text-xs font-bold bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md transition-all"
            >
              {printing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</> : <><FileText className="w-3.5 h-3.5" /> Print Selected ({Object.values(selected).filter(Boolean).length})</>}
            </button>
            <ArchiveButton
              taxYear={taxYear}
              category={type || "Transactions"}
              source="/api/reports/selected-pdf"
              sourceParams={{
                document_type: type,
                transaction_ids: Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
              }}
              titleSuffix={`${Object.values(selected).filter(Boolean).length} item${Object.values(selected).filter(Boolean).length === 1 ? "" : "s"}`}
              disabled={Object.values(selected).filter(Boolean).length === 0}
              testId="transactions-selected"
            />
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />{error}<button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {docs.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search filename..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="drilldown-search"
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>
          <select value={filterApproval} onChange={e => setFilterApproval(e.target.value)}
            data-testid="drilldown-approval-filter"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All approval states</option>
            <option value="none">Unapproved only</option>
            <option value="partial">Partially approved</option>
            <option value="all">Fully approved</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            data-testid="drilldown-status-filter"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="processed">Extracted</option>
            <option value="error">Error</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            data-testid="drilldown-sort"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="recent">Most recent</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Filename A–Z</option>
            <option value="amount">Largest amount</option>
            <option value="items">Most items</option>
          </select>
          {(search || filterApproval || filterStatus || sortBy !== "recent") && (
            <button onClick={() => { setSearch(""); setFilterApproval(""); setFilterStatus(""); setSortBy("recent"); }}
              className="text-slate-400 hover:text-slate-700 text-sm flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      )}

      {docs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <div className="text-center mb-6">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-semibold text-base" style={{ fontFamily: "Manrope" }}>No documents uploaded under "{type}" yet.</p>
            <p className="text-slate-500 text-sm mt-1">Upload one from the Upload &amp; Extract page to get started.</p>
          </div>
          <div className="border-t border-dashed border-slate-200 pt-5">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Example preview</p>
            <p className="text-xs text-slate-400 italic mb-3">Sample only — this is what your screen will look like after upload.</p>
            <div className="opacity-50 pointer-events-none select-none space-y-3">
              <SamplePreviewCard sub={SUBCATEGORIES.find(s => s.type === type) || {}} />
            </div>
          </div>
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm">
          <p className="text-slate-400 text-sm">No documents match your filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDocs.map(doc => {
            const items = txnsByDoc[doc._id] || [];
            const total = items.reduce((s, t) => s + Math.abs(t.amount || 0), 0);
            const approvedCount = items.filter(t => t.approved).length;
            const allApproved = items.length > 0 && approvedCount === items.length;
            const isOpen = !!expanded[doc._id];
            const StatusIcon = STATUS_ICON[doc.processing_status] || Loader;
            return (
              <div key={doc._id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded(prev => ({ ...prev, [doc._id]: !prev[doc._id] }))}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded(prev => ({ ...prev, [doc._id]: !prev[doc._id] })); }}
                  data-testid={`drilldown-doc-${doc._id}`}
                  className={`w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left cursor-pointer ${isOpen ? "bg-slate-50/60" : ""}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2">
                        <span className="truncate">{doc.original_name}</span>
                        <VaultProvenanceBadge sourceVaultFileId={doc.source_vault_file_id} />
                      </p>
                      <p className="text-xs text-slate-400 flex items-center gap-1.5">
                        {new Date(doc.upload_date).toLocaleDateString()}
                        <span className="text-slate-300">•</span>
                        <StatusIcon className={`w-3 h-3 ${STATUS_CLR[doc.processing_status]} ${doc.processing_status === "processing" ? "animate-spin" : ""}`} />
                        <span className={STATUS_CLR[doc.processing_status]}>
                          {doc.processing_status === "processed" ? "extracted" : doc.processing_status}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <span className="text-xs text-slate-500">{items.length} {items.length === 1 ? "item" : "items"}</span>
                    <span className="text-sm font-semibold text-slate-900">{fmt(total)}</span>
                    <span className={`text-xs font-semibold ${allApproved ? "text-emerald-600" : approvedCount > 0 ? "text-amber-600" : "text-slate-400"}`}>
                      {allApproved ? "✓ All approved" : `${approvedCount} of ${items.length}`}
                    </span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100">
                    {items.length === 0 ? (
                      <div className="px-5 py-8 text-center text-sm text-slate-400">
                        {doc.processing_status === "processing" ? (
                          <span className="flex items-center justify-center gap-2"><Loader className="w-3.5 h-3.5 animate-spin" /> Extracting items…</span>
                        ) : doc.processing_status === "error" ? (
                          <span className="text-red-500">Extraction failed. Try Retry from Upload &amp; Extract page.</span>
                        ) : doc.processing_status === "pending" ? "Waiting to process…" : "No line items extracted."}
                      </div>
                    ) : (
                      <Fragment>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wider">
                                <th className="px-3 py-2 w-10">
                                  <input
                                    type="checkbox"
                                    onChange={e => {
                                      const n = { ...selected };
                                      items.forEach(t => { n[t._id] = e.target.checked; });
                                      setSelected(n);
                                    }}
                                    checked={items.every(t => selected[t._id])}
                                    data-testid={`select-all-${doc._id}`}
                                  />
                                </th>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Category</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-center">Approved</th>
                                <th className="px-3 py-2"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map(t => (
                                <tr key={t._id} data-row-id={t._id} className={`border-b border-slate-50 hover:bg-slate-50/60 transition-all ${t.approved ? "bg-emerald-50/30" : ""}`}>
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={!!selected[t._id]}
                                      onChange={e => setSelected(prev => ({ ...prev, [t._id]: e.target.checked }))}
                                      data-testid={`select-txn-${t._id}`}
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                                    {t.moved && <ArrowRightLeft className="w-3 h-3 inline-block text-blue-500 mr-1" title="This line was moved from another card" />}
                                    {t.date}
                                  </td>
                                  <td className="px-3 py-2 text-slate-700">
                                    <input
                                      type="text"
                                      defaultValue={t.description || ""}
                                      onBlur={e => {
                                        const v = e.target.value;
                                        if (v !== (t.description || "")) updateTxn(t._id, { description: v });
                                      }}
                                      data-testid={`description-input-${t._id}`}
                                      className="w-full bg-transparent hover:bg-white focus:bg-white border border-transparent hover:border-slate-200 focus:border-[#2563EB] focus:outline-none rounded px-1.5 py-0.5 text-sm transition-colors"
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <input
                                      type="number"
                                      step="0.01"
                                      defaultValue={Math.abs(t.amount || 0).toFixed(2)}
                                      onBlur={e => {
                                        const v = parseFloat(e.target.value);
                                        if (!isNaN(v) && v !== Math.abs(t.amount || 0)) {
                                          updateTxn(t._id, { amount: v });
                                        }
                                      }}
                                      data-testid={`amount-input-${t._id}`}
                                      className="w-24 bg-transparent hover:bg-white focus:bg-white border border-transparent hover:border-slate-200 focus:border-[#2563EB] focus:outline-none rounded px-1.5 py-0.5 text-right font-semibold text-slate-900 transition-colors"
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={t.category || ""}
                                      onChange={e => updateTxn(t._id, { category: e.target.value })}
                                      data-testid={`category-select-${t._id}`}
                                      className="border border-slate-300 rounded px-2 py-1 text-xs bg-white max-w-[180px]"
                                    >
                                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                  </td>
                                  <td className="px-3 py-2">
                                    <select
                                      value={t.type || "expense"}
                                      onChange={e => updateTxn(t._id, { type: e.target.value })}
                                      data-testid={`type-select-${t._id}`}
                                      className={`border border-slate-300 rounded px-2 py-1 text-xs ${t.type === "income" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
                                    >
                                      <option value="income">Income</option>
                                      <option value="expense">Expense</option>
                                    </select>
                                  </td>
                                  <td className="px-3 py-2 text-center">
                                    <button
                                      onClick={() => updateTxn(t._id, { approved: !t.approved })}
                                      data-testid={`approve-btn-${t._id}`}
                                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mx-auto transition-colors ${t.approved ? "border-emerald-500 bg-emerald-500" : "border-slate-300 hover:border-emerald-400"}`}
                                    >
                                      {t.approved && <CheckCircle className="w-3 h-3 text-white" />}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <select
                                        value=""
                                        onChange={e => { moveTxn(t._id, e.target.value); e.target.value = ""; }}
                                        data-testid={`move-txn-${t._id}`}
                                        title="Move this line item to a different card"
                                        className="text-[10px] bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-500 rounded px-1.5 py-0.5 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#2563EB] max-w-[80px]"
                                      >
                                        <option value="">Move…</option>
                                        {SUBCATEGORIES.filter(s => s.type !== type).map(s => (
                                          <option key={s.type} value={s.type}>{s.icon} {s.type}</option>
                                        ))}
                                      </select>
                                      <button onClick={() => deleteTxn(t._id)} className="text-slate-300 hover:text-red-500 transition-colors"
                                        data-testid={`delete-txn-${t._id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="px-5 py-2 border-t border-slate-100 bg-white">
                            <button
                              onClick={() => addLineItem(doc._id)}
                              data-testid={`add-line-item-${doc._id}`}
                              className="text-xs font-medium text-[#2563EB] hover:text-[#1D4ED8] hover:bg-blue-50 px-3 py-1.5 rounded-md transition-all"
                            >
                              + Add Line Item
                            </button>
                            <span className="text-[11px] text-slate-400 ml-2">— for items the OCR missed or cash transactions</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 bg-slate-50/50 border-t border-slate-100">
                          <p className="text-xs text-slate-600">Subtotal: <span className="font-bold text-slate-900">{fmt(total)}</span></p>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => bulkApprove(items.filter(t => selected[t._id]).map(t => t._id), true)}
                              data-testid={`approve-checked-${doc._id}`}
                              disabled={!items.some(t => selected[t._id])}
                              className="text-xs bg-emerald-500 text-white px-3 py-1.5 rounded-md hover:bg-emerald-600 disabled:opacity-50"
                            >
                              ✓ Approve checked
                            </button>
                            <button
                              onClick={() => bulkApprove(items.filter(t => selected[t._id]).map(t => t._id), false)}
                              data-testid={`unapprove-checked-${doc._id}`}
                              disabled={!items.some(t => selected[t._id])}
                              className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1.5"
                            >
                              Un-approve checked
                            </button>
                            <div className="relative group">
                              <button
                                onClick={() => bulkApprove(items.map(t => t._id), true)}
                                data-testid={`approve-all-${doc._id}`}
                                aria-label="Approve all in this doc"
                                className="text-xs border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded-md hover:bg-emerald-50"
                              >
                                Approve all in this doc
                              </button>
                              <div
                                role="tooltip"
                                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-md bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              >
                                Approves every line item in this document so it flows into your totals. You can still edit, move, or un-approve any item anytime.
                              </div>
                            </div>
                            <div className="relative group">
                              <button
                                onClick={() => bulkApprove(items.map(t => t._id), false)}
                                data-testid={`unapprove-all-${doc._id}`}
                                aria-label="Un-approve all in this doc"
                                className="text-xs border border-slate-300 text-slate-600 px-3 py-1.5 rounded-md hover:bg-slate-100"
                              >
                                Un-approve all in this doc
                              </button>
                              <div
                                role="tooltip"
                                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-md bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              >
                                Removes approval from every line item in this document. Items are NOT deleted — they just stop counting in your totals until you re-approve them.
                              </div>
                            </div>
                            <div className="relative group">
                              <select
                                value=""
                                onChange={e => { bulkMove(items.filter(t => selected[t._id]).map(t => t._id), e.target.value); e.target.value = ""; }}
                                disabled={!items.some(t => selected[t._id])}
                                data-testid={`bulk-move-${doc._id}`}
                                aria-label="Move checked items to another document type"
                                className="text-xs bg-blue-50 border border-blue-200 text-blue-700 px-2.5 py-1.5 rounded-md hover:bg-blue-100 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                              >
                                <option value="">⇄ Move checked items</option>
                                {SUBCATEGORIES.filter(s => s.type !== type).map(s => (
                                  <option key={s.type} value={s.type}>{s.icon} {s.type}</option>
                                ))}
                              </select>
                              <div
                                role="tooltip"
                                className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 rounded-md bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              >
                                Re-categorizes the rows you've checked into a different document type (e.g., a row mis-filed as Bank Statement → Receipt/Invoice). Check items first to enable.
                              </div>
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// Faded sample card showing the user what an uploaded document with extracted line items will look like
function SamplePreviewCard({ sub }) {
  const sampleRows = sub.sampleLineItems || [
    { date: "01/05", desc: "STATE FARM AUTO PREMIUM", amt: 312.00, cat: "Insurance", type: "expense", approved: false },
    { date: "01/12", desc: "AETNA HEALTH PREMIUM", amt: 890.00, cat: "Insurance", type: "expense", approved: false },
    { date: "01/20", desc: "CACFP MONTHLY CLAIM", amt: 5200.00, cat: "CACFP Income", type: "income", approved: true },
  ];
  const subtotal = sampleRows.reduce((s, r) => s + r.amt, 0);
  const approvedCount = sampleRows.filter(r => r.approved).length;
  const sampleFile = sub.samples?.[0]?.name || "Sample_Document.pdf";

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="w-full flex items-center justify-between px-5 py-4 bg-slate-50/60">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate">{sampleFile}</p>
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              01/31/2025
              <span className="text-slate-300">•</span>
              <CheckCircle className="w-3 h-3 text-emerald-500" />
              <span className="text-emerald-500">extracted</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6 flex-shrink-0">
          <span className="text-xs text-slate-500">{sampleRows.length} items</span>
          <span className="text-sm font-semibold text-slate-900">{fmt(subtotal)}</span>
          <span className="text-xs font-semibold text-amber-600">{approvedCount} of {sampleRows.length}</span>
          <ChevronUp className="w-4 h-4 text-slate-400" />
        </div>
      </div>
      <div className="border-t border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wider">
              <th className="px-3 py-2 w-10"><input type="checkbox" disabled /></th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-center">Approved</th>
            </tr>
          </thead>
          <tbody>
            {sampleRows.map((r, idx) => (
              <tr key={idx} className={`border-b border-slate-50 ${r.approved ? "bg-emerald-50/30" : ""}`}>
                <td className="px-3 py-2"><input type="checkbox" disabled /></td>
                <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2 text-slate-700">{r.desc}</td>
                <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{fmt(r.amt)}</td>
                <td className="px-3 py-2">
                  <span className="border border-slate-300 rounded px-2 py-1 text-xs bg-white inline-block">{r.cat} ▾</span>
                </td>
                <td className="px-3 py-2">
                  <span className={`border border-slate-300 rounded px-2 py-1 text-xs inline-block ${r.type === "income" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                    {r.type === "income" ? "Income" : "Expense"} ▾
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`w-5 h-5 rounded-full border-2 inline-flex items-center justify-center ${r.approved ? "border-emerald-500 bg-emerald-500" : "border-slate-300"}`}>
                    {r.approved && <CheckCircle className="w-3 h-3 text-white" />}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-3 bg-slate-50/50 border-t border-slate-100">
        <p className="text-xs text-slate-600">Subtotal: <span className="font-bold text-slate-900">{fmt(subtotal)}</span></p>
        <div className="flex items-center gap-2">
          <span className="text-xs bg-emerald-500 text-white px-3 py-1.5 rounded-md">✓ Approve checked</span>
          <span className="text-xs border border-emerald-500 text-emerald-700 px-3 py-1.5 rounded-md bg-white">Approve all in this doc</span>
        </div>
      </div>
    </div>
  );
}
